import { useState } from "react";
import { Menu } from "@headlessui/react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import SearchBox from "components/__new/common/SearchBox";
import Table from "components/__new/common/Table";
import Filter from "components/__new/common/Filter";
import { ReactComponent as IconUser } from "assets/images/icons/project-icons/single-user.svg"
import { TableContent } from "components/__new/common/TableContent";
import Pagination from "components/__new/common/pagination";
import Maybe from "components/__new/common/Maybe";
import ContentLoading from "components/__new/common/ContentLoading";
import TableEmptyData from "components/__new/common/TableEmptyData";
import pluralize from "utils/pluralize";
import DropdownNew from "utils/dropdown";
import { ReactComponent as EditIcon } from "assets/images/icons/edit-3.svg"
import { ReactComponent as IconTrash } from "assets/images/icons/project-icons/trash-2.svg"
import { DeleteTransferBeneficiaryModal } from "./DeleteTransferBeneficiaryModal";


export const TransferBeneficiariesTable = ({addSingleBeneficiary, loading, data, getTransferBeneficiaries}) => {
    const { transferBeneficiariesLoading } = useSelector(s => s.entities.transfers);
    // eslint-disable-next-line
    const [detailsOpen, setDetailsOpen] = useState(false);
    // eslint-disable-next-line
    const [selected, setSelected] = useState(null);
    const [beneficiaryId, setBeneficiaryId] = useState("");
    const [viewBeneficiary, setViewBeneficiary] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [isOpenDeleteBeneficiary, setIsOpenDeleteBeneficiary] = useState(false);

    const toggleEditModal = (beneficiary) => {
        setViewBeneficiary((prev => ({...beneficiary})))
        setOpenEditModal(!openEditModal)
    };

    return (
        <div>
            <DeleteTransferBeneficiaryModal
                isOpen={isOpenDeleteBeneficiary}
                setIsOpen={setIsOpenDeleteBeneficiary}
                beneficiaryId={beneficiaryId}
                getTransferBeneficiaries={getTransferBeneficiaries}
            />

            <div className="mt-6 box">

                <div className="flex items-center justify-start w-max">
                    <div className="font-medium">Transfer Beneficiaries&nbsp;</div>    
                    {
                        data?.data?.totalDocs && (
                            <div className="label h-6 text-sm font-normal text-ep-primary-600 bg-[#ecf2f2]">
                                <ContentLoading
                                    loading={transferBeneficiariesLoading}
                                    data={<>
                                        {`${data?.data?.totalDocs ?? "-"} ${pluralize(data?.data?.totalDocs, "beneficiary", "beneficiaries")}`}
                                    </>}
                                />
                            </div>
                        )
                    }
                </div>
                
                <div className="mt-6 flex flex-wrap justify-between">
                    <div className="max-w-sm flex-grow sm:mr-6">
                        <SearchBox placeholder="Search" name="search" extraUrlParams="page=1" />
                    </div>
                        
                    <div className="flex flex-wrap space-x-3 sm:space-x-3">
                        <div>
                            <Filter filters={[
                                {
                                    title: "Date range",
                                    name: "dateRange",
                                    dataType: "custom-timeframe",
                                    hasResetButton: true,
                                    options: [
                                        {
                                            display: "Today",
                                            payload: "today",
                                        },
                                        {
                                            display: "Last 7 days",
                                            payload: "7days",
                                        },
                                        {
                                            display: "30 days",
                                            payload: "30days",
                                        },
                                        {
                                            display: "1 Year",
                                            payload: "1year",
                                        },
                                    ],
                                },
                                {
                                    title: "Custom Date Range",
                                    dataType: "date-range",
                                    options: {
                                        from: {
                                            name: "startDate",
                                            placeholder: "Start Date",
                                        },
                                        to: {
                                            name: "endDate",
                                            placeholder: "End Date",
                                        },
                                    },
                                },
                                {
                                    title: "Biller",
                                    name: "biller",
                                    dataType: "select",
                                    options: [
                                        {
                                            display: "All",
                                            payload: "",
                                        },
                                        {
                                            display: "Mtn",
                                            payload: "mtn",
                                        },
                                        {
                                            display: "Airtel",
                                            payload: "airtel",
                                        },
                                        {
                                            display: "9Mobile",
                                            payload: "9mobile",
                                        },
                                        {
                                            display: "Glo",
                                            payload: "glo",
                                        },
                                    ],
                                },
                                {
                                    title: "Transaction Status",
                                    name: "status",
                                    dataType: "select",
                                    options: [
                                        {
                                            display: "All",
                                            payload: "",
                                        },
                                        {
                                            display: "Successful",
                                            payload: "successful",
                                        },
                                        {
                                            display: "Pending",
                                            payload: "pending",
                                        },
                                        {
                                            display: "Failed",
                                            payload: "failed",
                                        },
                                    ],
                                },
                            ]}/>
                        </div>
                        
                        <NewBeneficiaryButton
                            buttonText="Add New Beneficiary"
                            addSingleBeneficiary={addSingleBeneficiary}
                        />
                        
                    </div>
                </div>
            </div>
                
            <div className="bg-white">

                <Table classTable="table-hover">
                    <Table.Head>
                        <th>Beneficiary Name</th>
                        <th>Bank Name</th>
                        <th>Account Number</th>
                        <th></th>
                    </Table.Head>
                    <Table.Body>

                        <TableContent
                            loading={transferBeneficiariesLoading}
                            loadingText="Fetching Transfer Beneficiaries"
                            data={data?.data?.docs}
                            colspan={5}
                            emptyUI={
                                <TableEmptyData 
                                    actionButtonClicked={addSingleBeneficiary}
                                    actionButtonText="Click here to add new beneficiary"
                                />
                            }
                        />

                        {loading === false && data?.data?.docs?.map((beneficiary) => {
                            
                            return (
                                <tr key={beneficiary?._id}>
                                    <td>
                                        {beneficiary?.alias ?? "-"}
                                    </td>
                                    <td>
                                        {beneficiary?.bankName ?? "-"}
                                    </td>
                                    <td>
                                        {beneficiary?.accountNumber ?? "-"}
                                    </td>
                                    <td>
                                        <div>
                                            {<DropdownNew flipSwitch={true}>
                                                <></>
                                                <div>
                                                    {/* <button type="button" onClick={() => toggleEditModal(beneficiary)} className="dropdown-item dropdown-item-icon text-left">
                                                    <EditIcon /> Edit
                                                    </button>  */}
                                                    <button type="button" onClick={() => {
                                                        setBeneficiaryId(beneficiary._id);
                                                        setIsOpenDeleteBeneficiary(true)
                                                    }} className="dropdown-item dropdown-item-icon text-ep-red-500 text-left">
                                                        <IconTrash /> Delete
                                                    </button>
                                                </div>
                                            </DropdownNew>}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        )}
                    </Table.Body>
                </Table>

                <Maybe condition={loading === false && data?.data?.docs.length > 0}>
                    <div className="mt-6">
                        <Pagination data={data?.data} />
                    </div>
                </Maybe>

            </div>
        </div>
    )
}


const NewBeneficiaryButton = ({buttonText, addSingleBeneficiary}) => {
    return (
        <Menu as='div'>
            
            <Menu.Button className="">
                <div className="btn btn-ep-primary flex items-center justify-center">
                    <span>{buttonText}</span>
                    <FontAwesomeIcon icon="angle-down" fontSize={108} />
                </div>
            </Menu.Button>
            
            <Menu.Items as='div' className='dropdown'>
                <Menu.Item as='div' onClick={addSingleBeneficiary}>
                    <div className="dropdown-item flex items-center">
                        <IconUser />
                        <span className="ml-1">Add Single Beneficiary</span>
                    </div>
                </Menu.Item>
                
                {/* <Menu.Item>
                    <div className="dropdown-item flex items-center">
                        <IconUsers />
                        <span className="ml-1">Add Bulk Beneficiaries</span>
                    </div>
                </Menu.Item> */}
            </Menu.Items>
            
        </Menu>
    )
}


export default TransferBeneficiariesTable;